import { Link } from 'react-router-dom';
import { Aperture, ArrowRight, FileVideo, Projector } from 'lucide-react';
import { CATALOG_COURSES, formatCoursePrice } from '../../data/catalog';
import Reveal from './Reveal';

const PROGRAM_META = {
  cyn_basics: {
    status: 'In development',
    blurb: 'Explore the fundamentals and understand the different areas of filmmaking.',
    tags: ['Film language', 'Story', 'Cinematography'],
    cta: 'Get early access',
  },
  cyn_direction: {
    status: 'Launching soon',
    featured: true,
    blurb:
      'Learn from an experienced feature-film director and apply what you learn through practical assignments.',
    tags: ['Live-action concepts', 'Practical assignments', '1–2 min short film'],
    cta: 'Join waitlist',
  },
  cyn_editing: {
    status: 'In development',
    blurb: 'Explore the fundamentals and understand the craft of film editing.',
    tags: ['Editing fundamentals', 'Story in the edit', 'Tools & techniques'],
    cta: 'Get early access',
  },
  cyn_screenwriting: {
    status: 'In development',
    blurb: 'Every great film begins on the page - learn story structure, character, and screenplay craft.',
    tags: ['Story structure', 'Character & dialogue', 'Screenplay format'],
    cta: 'Get early access',
  },
};

const UPCOMING_DISCIPLINES = [
  { icon: Projector, label: 'Film Production' },
  { icon: Aperture, label: 'Cinematography' },
  { icon: FileVideo, label: 'Documentary Filmmaking' },
];

const Programs = ({ onWaitlist }) => (
  <section
    data-testid="popular-courses-section"
    className="relative bg-[#080808] py-20 md:py-28"
    id="programs"
  >
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <Reveal className="mb-14 text-center">
        <h2 className="display-heading text-3xl text-white sm:text-4xl lg:text-5xl">
          Our filmmaking <em>programs.</em>
        </h2>
      </Reveal>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {CATALOG_COURSES.map((course, idx) => {
          const meta = PROGRAM_META[course.course_id] || {};
          const price = formatCoursePrice(course);

          return (
            <Reveal key={course.course_id} delay={idx * 0.1} className="h-full">
              <article
                data-testid={`popular-course-card-${idx}`}
                className={`home-card flex h-full flex-col overflow-hidden ${
                  meta.featured ? 'home-card--featured' : ''
                }`}
              >
                <Link
                  to={`/courses/${course.course_id}`}
                  className="group relative block aspect-[16/10] overflow-hidden"
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  <img
                    src={course.thumbnail}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C0C] via-black/30 to-black/20" />
                  <span
                    className={`home-status absolute left-4 top-4 ${
                      meta.featured ? 'home-status--live' : ''
                    }`}
                  >
                    {meta.featured && (
                      <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-white" />
                    )}
                    {meta.status}
                  </span>
                </Link>

                <div className="flex flex-1 flex-col p-6 md:p-7">
                  <h3 className="mb-3 font-serif text-xl leading-snug text-white">
                    <Link
                      to={`/courses/${course.course_id}`}
                      className="transition-colors hover:text-[#C8102E]"
                    >
                      {course.title}
                    </Link>
                  </h3>

                  <p className="mb-6 text-sm leading-relaxed text-zinc-400">{meta.blurb}</p>

                  <ul className="mb-8 flex flex-wrap gap-2">
                    {(meta.tags || []).map((tag) => (
                      <li key={tag} className="home-tag">
                        {tag}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto">
                    {price && (
                      <div className="mb-5">
                        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                          Starts from
                        </p>
                        <p className="font-serif text-2xl text-white">{price}</p>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => onWaitlist(course.title)}
                      data-testid={`program-cta-${course.course_id}`}
                      className={`w-full justify-center rounded-md ${
                        meta.featured ? 'btn-cine-primary' : 'btn-cine-outline'
                      }`}
                    >
                      {meta.cta}
                    </button>
                  </div>
                </div>
              </article>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.15} className="mt-14">
        <p className="mb-5 text-center text-[0.7rem] uppercase tracking-[0.25em] text-zinc-500">
          More disciplines in the works
        </p>
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {UPCOMING_DISCIPLINES.map(({ icon: Icon, label }) => (
            <li
              key={label}
              className="home-card flex items-center gap-3 px-5 py-4 text-sm text-zinc-300"
            >
              <Icon className="h-5 w-5 flex-shrink-0 text-zinc-500" strokeWidth={1.4} />
              {label}
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={0.2} className="mt-12 text-center">
        <Link
          to="/courses"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-[#C8102E]"
        >
          View all courses
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default Programs;
